import { useAsync } from 'react-use';

import { CatalogApi } from '@backstage/catalog-client';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

export const useCatalogCounts = () => {
  const identityApi = useApi(identityApiRef);
  const catalogApi: CatalogApi = useApi(catalogApiRef);

  const { value: catalogCounts, loading: catalogCountsLoading } =
    useAsync(async () => {
      const { userEntityRef: currentUserEntityRef } =
        await identityApi.getBackstageIdentity();
      const user = await catalogApi.getEntityByRef(currentUserEntityRef);

      if (!user) {
        return {
          teams: 0,
          services: 0,
          repositories: 0,
          apis: 0,
        };
      }

      const memberOfRelations = user.relations?.filter(
        r => r.type === 'memberOf' && r.targetRef.startsWith('group:'),
      );
      const groupNames =
        memberOfRelations
          ?.map(r => {
            const match = r.targetRef.match(/^group:[^/]+\/(.+)$/);
            return match ? match[1] : '';
          })
          .filter(Boolean) || [];

      let ownedRepositoriesCount = 0;
      let ownedServicesCount = 0;
      let ownedApisCount = 0;
      const ownedTeamsCount = groupNames.length;

      if (groupNames.length > 0) {
        const repositoryFilter = {
          kind: 'Repository',
          'relations.ownedBy': groupNames.map(name => `group:default/${name}`),
        };
        const repositories = await catalogApi.getEntities({
          filter: repositoryFilter,
        });
        ownedRepositoriesCount = repositories.items.length;

        const serviceFilter = {
          kind: 'Component',
          'spec.type': 'service',
          'relations.ownedBy': groupNames.map(name => `group:default/${name}`),
        };
        const services = await catalogApi.getEntities({
          filter: serviceFilter,
        });
        ownedServicesCount = services.items.length;

        const apiFilter = {
          kind: 'API',
          'relations.ownedBy': groupNames.map(name => `group:default/${name}`),
        };
        const apis = await catalogApi.getEntities({ filter: apiFilter });
        ownedApisCount = apis.items.length;
      }

      return {
        teams: ownedTeamsCount,
        services: ownedServicesCount,
        repositories: ownedRepositoriesCount,
        apis: ownedApisCount,
      };
    }, [catalogApi, identityApi]);

  return { catalogCounts, catalogCountsLoading };
};
